import React, { useState } from 'react';
import { getCurrentUser } from '../firebase/authService';
import { addGuestNote, addVolunteerNote } from '../firebase/firestoreService';
import { useAppBusy } from '../utils/appUpdate';
import { readOnlyProps } from '../utils/viewAs';
import '../styles/AddNoteModal.css';

// kind="volunteer" saves to volunteerNotes (from Volunteer Profiles). Some
// people are both guests and volunteers; the page the note is added from
// decides which record it belongs to.
function AddNoteModal({ guest, onClose, onSave, kind = 'guest' }) {
  const currentUser = getCurrentUser();
  const ro = readOnlyProps(); // disable write controls while viewing as someone
  const [noteText, setNoteText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Mounted only while open; a reload would discard the note being typed.
  useAppBusy('add-note', true);
  const [message, setMessage] = useState(null);

  const handleSubmit = async () => {
    if (!noteText.trim()) {
      setMessage({
        type: 'error',
        text: 'Please enter a note before submitting.'
      });
      return;
    }

    setSubmitting(true);
    setMessage(null);

    try {
      const createdByName = currentUser.displayName || currentUser.email;
      if (kind === 'volunteer') {
        await addVolunteerNote({
          volunteerId: guest.id,
          volunteerFirstName: guest.firstName,
          volunteerLastName: guest.lastName,
          noteText: noteText.trim(),
          createdBy: currentUser.uid,
          createdByName
        });
      } else {
        await addGuestNote({
          guestId: guest.id,
          guestFirstName: guest.firstName,
          guestLastName: guest.lastName,
          noteText: noteText,
          createdBy: currentUser.uid,
          createdByName
        });
      }

      setMessage({
        type: 'success',
        text: 'Note added successfully!'
      });

      setNoteText('');

      setTimeout(() => {
        if (onSave) {
          onSave();
        }
        onClose();
      }, 800);
    } catch (error) {
      console.error('Error adding note:', error);
      setMessage({
        type: 'error',
        text: `Failed to add note: ${error.message}`
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content add-note-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add Note - {guest.firstName} {guest.lastName}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {message && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}

        <div className="modal-body">
          {/* Guest Info Section */}
          <div className="guest-info-section">
            {guest.image && (
              <img
                src={guest.image}
                alt={`${guest.firstName} ${guest.lastName}`}
                className="guest-avatar-modal"
              />
            )}
            <div className="guest-details-modal">
              <h3>{guest.firstName} {guest.lastName}</h3>
              {guest.email && <p className="guest-email">{guest.email}</p>}
              {guest.phone && <p className="guest-phone">{guest.phone}</p>}
            </div>
          </div>

          {/* Add Note Section */}
          <div className="add-note-section">
            <h4>Add New Note</h4>
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Enter your note here..."
              rows="4"
              maxLength={kind === 'volunteer' ? 1000 : undefined}
              className="note-textarea"
              disabled={submitting}
            />
          </div>
        </div>

        <div className="modal-footer">
          <button
            className="btn btn-secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={submitting || !noteText.trim() || ro.disabled}
            title={ro.title}
          >
            {submitting ? 'Adding Note...' : 'Add Note'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddNoteModal;
